// SPEC-IN-APP-FEEDBACK-01 (D-852): 現場からアプリ内で 不具合・要望 を送る。
//   送り先 = RPC fn_feedback_submit(表 app_feedback に 1 行)。Discord への一報は DB のトリガが出す
//   (クライアントに webhook は置かない・VITE_ の秘密は使わない)。
//   写真は非公開バケット app-feedback の {staff_id}/ の下にだけ上げる(ポリシーが本人の接頭辞以外を拒む)。
//
//   ★自動で添えるのは 画面のパス(? と # より後は捨てる)/ 版と sha / 端末の粗い種別 だけ。
//     UA の全文・氏名・電話・トークンは送らない(D-838 authTrace と同じ作法)。staff_id と組織はサーバが JWT から埋める。
import { supabase } from '../lib/supabase'
import { BUILD_VERSION, BUILD_SHA } from '../lib/buildInfo'
import { isOfflineContinuation } from '../lib/auth/offlineGate'
import { encodePrizeImageToJpeg } from './prizes'

export const FEEDBACK_BUCKET = 'app-feedback'
export const FEEDBACK_MAX_LEN = 4000
export const URGENCY = Object.freeze({ normal: 'normal', urgent: 'urgent' })
const DRAFT_KEY = 'd852_feedback_draft'

/** 端末の粗い種別。機種名・OS 版は取らない。 */
export function deviceKindFromUA(ua) {
  const s = typeof ua === 'string' ? ua : ''
  if (/iPhone|iPad|iPod/i.test(s)) return 'ios'
  if (/Android/i.test(s)) return 'android'
  if (/Windows|Macintosh|X11|Linux/i.test(s)) return 'pc'
  return 'other'
}

/** 画面のパスから ? と # より後を落とす(招待トークン等のクエリを持ち込まない)。 */
export function sanitizeRoute(pathname) {
  if (typeof pathname !== 'string' || pathname === '') return null
  const p = pathname.split('?')[0].split('#')[0]
  return p ? p.slice(0, 200) : null
}

/** 自動で添える文脈。ここに足すものはテスト(appFeedbackD852.test.js)で個人情報が入らないことを固定している。 */
export function buildFeedbackContext({ pathname, userAgent } = {}) {
  let ua = userAgent
  if (ua === undefined) {
    try { ua = navigator.userAgent } catch { ua = '' }
  }
  return {
    route: sanitizeRoute(pathname),
    app_version: BUILD_VERSION,
    app_sha: BUILD_SHA,
    device_kind: deviceKindFromUA(ua),
  }
}

function newRequestId() {
  try { if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID() } catch { /* fallthrough */ }
  // randomUUID が無い古い端末向け(形式だけ uuid v4)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function isOfflineNow() {
  try { if (typeof navigator !== 'undefined' && navigator.onLine === false) return true } catch { /* ignore */ }
  try { if (isOfflineContinuation()) return true } catch { /* ignore */ }
  return false
}

// 通信そのものが届かなかった失敗(圏外・守衛の 503)か。
function isNetworkError(err, status) {
  if (status === 503 || status === 0) return true
  const m = String(err?.message ?? err ?? '')
  return /Failed to fetch|NetworkError|Load failed|network/i.test(m)
}

/**
 * 送信する。
 * @param {{ body: string, urgency?: 'normal'|'urgent', photoFile?: File|Blob|null, staffId: string,
 *           pathname?: string, storeCode?: string|null, requestId?: string }} p
 * @returns {Promise<{ status: 'ok'|'offline'|'invalid'|'error', id?: string, message?: string, requestId: string }>}
 *   requestId は再送時に同じ値を渡す(サーバが 1 行にまとめ、Discord も 2 通にならない)。
 */
export async function submitFeedback({ body, urgency = URGENCY.normal, photoFile = null, staffId, pathname, storeCode = null, requestId } = {}) {
  const rid = requestId || newRequestId()
  const text = typeof body === 'string' ? body.trim() : ''
  if (!text) return { status: 'invalid', message: '内容を書いてください', requestId: rid }
  if (text.length > FEEDBACK_MAX_LEN) return { status: 'invalid', message: `${FEEDBACK_MAX_LEN} 文字までです`, requestId: rid }
  if (urgency !== URGENCY.normal && urgency !== URGENCY.urgent) return { status: 'invalid', message: '急ぎ / ふつう を選んでください', requestId: rid }
  if (!staffId) return { status: 'error', message: 'ログインし直してください', requestId: rid }
  if (isOfflineNow()) return { status: 'offline', requestId: rid }

  try {
    let photoPath = null
    if (photoFile) {
      const { blob } = await encodePrizeImageToJpeg(photoFile)
      photoPath = `${staffId}/${rid}.jpg`
      const { error: upErr } = await supabase.storage
        .from(FEEDBACK_BUCKET)
        .upload(photoPath, blob, { contentType: 'image/jpeg', upsert: false })
      // 再送で同じパスが既にある(前回は写真だけ上がって RPC で落ちた)なら、そのまま使う。
      if (upErr && !/exists|Duplicate/i.test(String(upErr.message ?? ''))) {
        if (isNetworkError(upErr, upErr.statusCode ? Number(upErr.statusCode) : undefined)) return { status: 'offline', requestId: rid }
        return { status: 'error', message: '写真を送れませんでした', requestId: rid }
      }
    }

    const ctx = buildFeedbackContext({ pathname })
    const { data, error, status } = await supabase.rpc('fn_feedback_submit', {
      p_body: text,
      p_urgency: urgency,
      p_route: ctx.route,
      p_app_version: ctx.app_version,
      p_app_sha: ctx.app_sha,
      p_device_kind: ctx.device_kind,
      p_store_code: storeCode || null,
      p_photo_path: photoPath,
      p_client_request_id: rid,
    })
    if (error) {
      if (isNetworkError(error, status)) return { status: 'offline', requestId: rid }
      return { status: 'error', message: '送れませんでした', requestId: rid }
    }
    // ★supabase-js は権限で弾かれても error なしで返ることがある(D-298)。中身の status で判定する。
    if (data?.status !== 'ok' || !data?.id) {
      return { status: 'error', message: '送れませんでした', requestId: rid }
    }
    return { status: 'ok', id: data.id, requestId: rid }
  } catch (e) {
    if (isNetworkError(e)) return { status: 'offline', requestId: rid }
    return { status: 'error', message: e?.message ? String(e.message).slice(0, 80) : '送れませんでした', requestId: rid }
  }
}

// ── D-889: 送った本人が返信を見る ──────────────────────────────
//   読み: 既存の RLS app_feedback_select(d852a)が本人の行を許す。★admin/manager は RLS で全件見えるので、
//   「自分が送った分」にするため staff_id で必ず絞る(organization_id ではない = RLS 違反の絞りではない)。
//   既読: RPC fn_feedback_mark_seen だけ(本人の行・対応済み・未読 の reporter_seen_at だけをサーバが付ける)。
export const MY_FEEDBACK_LIMIT = 20
const MY_COLS = 'id, created_at, body, urgency, route, photo_path, handled_at, response_note, reporter_seen_at'

/**
 * 自分が送った 不具合・要望(新しい順・最大 MY_FEEDBACK_LIMIT 件)。写真はパスを渡さず「有無」だけにする。
 * @returns {Promise<{ data: object[], error: Error|null }>}  ★投げない
 */
export async function listMyFeedback(staffId) {
  if (!staffId) return { data: [], error: null }
  try {
    const { data, error } = await supabase
      .from('app_feedback')
      .select(MY_COLS)
      .eq('staff_id', staffId)
      .order('created_at', { ascending: false })
      .limit(MY_FEEDBACK_LIMIT)
    if (error) return { data: [], error }
    const rows = (data ?? []).map(({ photo_path, ...r }) => ({ ...r, has_photo: !!photo_path }))
    return { data: rows, error: null }
  } catch (e) {
    return { data: [], error: e }
  }
}

/** 対応済み かつ まだ本人が見ていない行か(バッジと既読の対象を決める唯一の判定)。 */
function isUnseenReply(row) {
  return !!row && !!row.handled_at && !row.reporter_seen_at
}

/** 一覧のうち 既読にする id(対応済み かつ 未読)。 */
export function unseenReplyIds(rows) {
  return (rows ?? []).filter(isUnseenReply).map(r => r.id)
}

/**
 * バッジ件数 = 自分の行で handled_at が入っていて reporter_seen_at が空のもの。件数だけ(head:true)を取る。
 * @returns {Promise<number|null>}  引けなければ null(バッジを出さない)。★投げない
 */
export async function countUnseenReplies(staffId) {
  if (!staffId) return null
  try {
    const { count, error } = await supabase
      .from('app_feedback')
      .select('id', { count: 'exact', head: true })
      .eq('staff_id', staffId)
      .not('handled_at', 'is', null)
      .is('reporter_seen_at', null)
    if (error) return null
    return count ?? 0
  } catch {
    return null
  }
}

/**
 * 返信を見た印を付ける(RPC だけ。表への直 UPDATE はしない)。
 * @returns {Promise<{ status: 'ok'|'skip'|'error', count?: number }>}  ★投げない
 */
export async function markFeedbackSeen(ids) {
  const list = (ids ?? []).filter(Boolean)
  if (list.length === 0) return { status: 'skip' }
  try {
    const { data, error } = await supabase.rpc('fn_feedback_mark_seen', { p_ids: list })
    if (error || data?.status !== 'ok') return { status: 'error' }
    return { status: 'ok', count: Number(data.count ?? 0) }
  } catch {
    return { status: 'error' }
  }
}

// ── 下書き(この端末の中だけ。送れなかったときに消さないため)──
export function loadFeedbackDraft() {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null')
    if (d && typeof d.body === 'string') {
      return { body: d.body, urgency: d.urgency === URGENCY.urgent ? URGENCY.urgent : URGENCY.normal, requestId: typeof d.requestId === 'string' ? d.requestId : null }
    }
  } catch { /* ignore */ }
  return null
}
export function saveFeedbackDraft({ body, urgency, requestId } = {}) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ body: body ?? '', urgency: urgency ?? URGENCY.normal, requestId: requestId ?? null })) } catch { /* ignore */ }
}
export function clearFeedbackDraft() {
  try { localStorage.removeItem(DRAFT_KEY) } catch { /* ignore */ }
}
